/**
 * NFeListPage Component
 * 
 * Página de listagem e gerenciamento de NF-e modelo 55.
 * Permite visualizar, emitir e gerenciar Notas Fiscais Eletrônicas.
 * 
 * Funcionalidades:
 * - Listagem de NF-e com paginação
 * - Busca por número, chave de acesso ou cliente
 * - Ações: visualizar, reenviar, baixar PDF, cancelar
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Search,
  MoreVertical,
  X, Loader2, AlertCircle
} from 'lucide-react'
import { useToast } from '../../../../hooks/useToast'
import DataTable from '../../../../components/ERP/DataTable/DataTable'
import { nfeService, customerService } from '../../../../services/erpApi'
import api from '../../../../services/api'
import ConfirmModal from '../../../../components/ERP/ConfirmModal/ConfirmModal'
import NFeStatusBadge from '../../../../components/ERP/NFe/NFeStatusBadge'
import NFeCancelModal from '../../../../components/ERP/NFe/NFeCancelModal'
import NFeCorrigirRazaoSocialModal, { RAZAO_SOCIAL_CANCEL_JUSTIFICATION } from '../../../../components/ERP/NFe/NFeCorrigirRazaoSocialModal'
import NFeCartaCorrecaoModal from '../../../../components/ERP/NFe/NFeCartaCorrecaoModal'
import NFeDropdownMenu from '../../../../components/ERP/NFe/NFeDropdownMenu'
import Modal from '../../../../components/ERP/Modal/Modal'
import { Toast } from '../../../../components'
import './NFeListPage.css'

function NFeListPage() {
  const navigate = useNavigate()
  const { toast, showToast, hideToast } = useToast()
  const [nfeList, setNfeList] = useState([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  })
  const [filters, setFilters] = useState({
    search: ''
  })
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  })
  const [, setCustomers] = useState([])
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [cancelInitialJustification, setCancelInitialJustification] = useState('')
  const [corrigirRazaoModalOpen, setCorrigirRazaoModalOpen] = useState(false)
  const [savingRazaoSocial, setSavingRazaoSocial] = useState(false)
  const [cartaCorrecaoModalOpen, setCartaCorrecaoModalOpen] = useState(false)
  const [selectedNFe, setSelectedNFe] = useState(null)
  const [cancelling, setCancelling] = useState(false)
  const [emittingCCe, setEmittingCCe] = useState(false)
  const [, setConsultingStatus] = useState(null)
  const [openDropdown, setOpenDropdown] = useState(null)
  const [dropdownButtonRefs, setDropdownButtonRefs] = useState({})
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [nfeDetails, setNfeDetails] = useState(null)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [danfeModalOpen, setDanfeModalOpen] = useState(false)
  const [danfeUrl, setDanfeUrl] = useState(null)
  const [danfeNFeNumber, setDanfeNFeNumber] = useState(null)
  const [danfeLoading, setDanfeLoading] = useState(true)
  const [danfeError, setDanfeError] = useState(null)
  const [danfeBlobUrl, setDanfeBlobUrl] = useState(null)

  useEffect(() => {
    loadNFeList()
    loadCustomers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.page])

  // Função para carregar PDF como blob
  const loadPdfAsBlob = useCallback(async (url) => {
    try {
      setDanfeLoading(true)
      setDanfeError(null)
      
      // Baixar PDF com autenticação
      const response = await api.get(url, {
        responseType: 'blob'
      })
      
      // Criar blob URL
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const urlBlob = URL.createObjectURL(blob)
      
      // Limpar blob URL anterior se existir
      if (danfeBlobUrl) {
        URL.revokeObjectURL(danfeBlobUrl)
      }
      
      setDanfeBlobUrl(urlBlob)
      setDanfeLoading(false)
    } catch (error) {
      let errorMessage = 'Erro ao carregar o DANFE. Verifique se a NF-e está autorizada.'
      if (error.response?.status === 401) {
        errorMessage = 'Erro de autenticação. Faça login novamente.'
      } else if (error.response?.status === 404) {
        errorMessage = 'PDF não encontrado. A NF-e pode ainda estar sendo processada.'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      }
      
      setDanfeError(errorMessage)
      setDanfeLoading(false)
    }
  }, [danfeBlobUrl])

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Não fechar se o clique foi no dropdown menu (renderizado via portal)
      if (event.target.closest('.nfe-dropdown-menu-fixed')) {
        return
      }
      
      if (openDropdown !== null && !event.target.closest('.dropdown-wrapper')) {
        setOpenDropdown(null)
      }
    }

    // Usar um pequeno delay para não interferir com cliques nos botões do menu
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openDropdown])

  // Carregar PDF do DANFE quando o modal abrir
  useEffect(() => {
    if (danfeModalOpen && danfeUrl) {
      setDanfeLoading(true)
      setDanfeError(null)
      
      // Limpar blob URL anterior se existir
      if (danfeBlobUrl) {
        URL.revokeObjectURL(danfeBlobUrl)
        setDanfeBlobUrl(null)
      }
      
      // Se a URL é do nosso backend, baixar o PDF e criar blob URL
      if (danfeUrl.includes('/api/') || danfeUrl.includes('localhost:3000')) {
        loadPdfAsBlob(danfeUrl)
      } else {
        // URL externa - carregar diretamente
        setDanfeLoading(false)
      }
    }
    
    // Limpar blob URL ao fechar o modal
    return () => {
      if (danfeBlobUrl) {
        URL.revokeObjectURL(danfeBlobUrl)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [danfeModalOpen, danfeUrl, loadPdfAsBlob])

  const loadNFeList = async () => {
    setLoading(true)
    try {
      const response = await nfeService.getAll({
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      })
      
      if (response.success) {
        const validNFe = (response.data || []).filter(item => 
          item && item !== null && typeof item === 'object'
        )
        setNfeList(validNFe)
        if (response.pagination) {
          setPagination(response.pagination)
        }
      }
    } catch (error) {
      console.error('Erro ao carregar NF-e:', error)
      showToast('Erro ao carregar lista de NF-e', 'error')
    } finally {
      setLoading(false)
    }
  }

  const loadCustomers = async () => {
    try {
      const response = await customerService.getAll({ status: 'active' })
      if (response.success) {
        setCustomers(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error)
    }
  }

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handleCancel = (nfe) => {
    setSelectedNFe(nfe)
    setCancelInitialJustification('')
    setCancelModalOpen(true)
  }

  const handleConfirmRazaoSocial = async (name) => {
    if (!selectedNFe) return false
    setSavingRazaoSocial(true)
    try {
      const response = await nfeService.corrigirRazaoSocial(selectedNFe.id, name)
      if (response.success) {
        showToast(response.message || 'Razão social atualizada.', 'success')
        setSelectedNFe((current) => current ? { ...current, customer_name: name } : current)
        loadNFeList()
        return true
      }
      showToast(response.message || 'Erro ao corrigir a razão social', 'error')
      return false
    } catch (error) {
      console.error('Erro ao corrigir razão social:', error)
      showToast(error.response?.data?.message || 'Erro ao corrigir a razão social', 'error')
      return false
    } finally {
      setSavingRazaoSocial(false)
    }
  }

  const handleConfirmCancel = async (justificativa) => {
    if (!selectedNFe) return

    setCancelling(true)
    try {
      const response = await nfeService.cancelar(selectedNFe.id, justificativa)
      if (response.success) {
        showToast('NF-e cancelada com sucesso!', 'success')
        setCancelModalOpen(false)
        setSelectedNFe(null)
        loadNFeList()
      } else {
        showToast(response.message || 'Erro ao cancelar NF-e', 'error')
      }
    } catch (error) {
      console.error('Erro ao cancelar NF-e:', error)
      showToast(error.response?.data?.message || 'Erro ao cancelar NF-e', 'error')
    } finally {
      setCancelling(false)
    }
  }

  const handleConsultarStatus = async (nfe) => {
    setConsultingStatus(nfe.id)
    try {
      const response = await nfeService.consultarStatus(nfe.id)
      if (response.success) {
        showToast('Status consultado com sucesso!', 'success')
        loadNFeList() // Recarregar para atualizar status
      } else {
        showToast(response.message || 'Erro ao consultar status', 'error')
      }
    } catch (error) {
      console.error('Erro ao consultar status:', error)
      showToast(error.response?.data?.message || 'Erro ao consultar status', 'error')
    } finally {
      setConsultingStatus(null)
    }
  }

  const handleVisualizarDANFE = async (nfe) => {
    try {
      // Obter URL do DANFE
      const response = await nfeService.visualizarDANFE(nfe.id)
      
      if (response && response.success && response.url) {
        // Abrir modal com o DANFE
        setDanfeUrl(response.url)
        setDanfeNFeNumber(nfe.number || nfe.id)
        setDanfeModalOpen(true)
        setDanfeError(null)
      } else {
        throw new Error('URL do DANFE não disponível na resposta')
      }
    } catch (error) {
      console.error('Erro ao visualizar DANFE:', error)
      // Tratar diferentes tipos de erro
      let errorMessage = 'Erro ao visualizar DANFE. A NF-e pode ainda estar sendo processada.'
      
      if (error.message) {
        errorMessage = error.message
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error
      }
      
      showToast(errorMessage, 'error')
    }
  }

  const handleCloseDanfeModal = () => {
    setDanfeModalOpen(false)
    setDanfeUrl(null)
    setDanfeNFeNumber(null)
    setDanfeError(null)
    if (danfeBlobUrl) {
      URL.revokeObjectURL(danfeBlobUrl)
      setDanfeBlobUrl(null)
    }
  }

  const handleRetryDanfe = () => {
    setDanfeError(null)
    setDanfeLoading(true)
    if (danfeUrl) {
      if (danfeUrl.includes('/api/') || danfeUrl.includes('localhost:3000')) {
        loadPdfAsBlob(danfeUrl)
      } else {
        // Para URLs externas, recarregar iframe
        const iframe = document.getElementById('danfe-iframe')
        if (iframe) {
          iframe.src = danfeUrl
        }
        setDanfeLoading(false)
      }
    }
  }

  const handleTransmitNFe = async (nfe) => {
    setSelectedNFe(nfe)
    setConfirmModal({
      isOpen: true,
      title: 'Transmitir NF-e',
      message: `Deseja transmitir a NF-e #${nfe.number || nfe.id}? Esta ação irá emitir a nota fiscal na SEFAZ.`,
      onConfirm: async () => {
        try {
          setConfirmModal({ ...confirmModal, isOpen: false })
          
          // Chamar endpoint de transmissão
          const response = await nfeService.transmitirNota(nfe.id)
          
          if (response.success) {
            showToast('NF-e transmitida com sucesso!', 'success')
            loadNFeList()
          } else {
            showToast(response.message || 'Erro ao transmitir NF-e', 'error')
          }
        } catch (error) {
          console.error('Erro ao transmitir NF-e:', error)
          showToast(error.response?.data?.message || 'Erro ao transmitir NF-e', 'error')
        }
      }
    })
  }

  const handleConfirmCartaCorrecao = async (correcao) => {
    if (!selectedNFe) return

    setEmittingCCe(true)
    try {
      const response = await nfeService.emitirCartaCorrecao(selectedNFe.id, correcao)
      if (response.success) {
        showToast('Carta de Correção emitida com sucesso!', 'success')
        setCartaCorrecaoModalOpen(false)
        setSelectedNFe(null)
        loadNFeList()
      } else {
        showToast(response.message || 'Erro ao emitir Carta de Correção', 'error')
      }
    } catch (error) {
      console.error('Erro ao emitir Carta de Correção:', error)
      showToast(error.response?.data?.message || 'Erro ao emitir Carta de Correção', 'error')
    } finally {
      setEmittingCCe(false)
    }
  }

  const handleViewNFe = async (nfe) => {
    if (!nfe || !nfe.id) {
      showToast('NF-e inválida', 'error')
      return
    }
    
    setSelectedNFe(nfe)
    setViewModalOpen(true)
    setLoadingDetails(true)
    setNfeDetails(null)
    
    try {
      const response = await nfeService.getById(nfe.id)
      if (response.success) {
        setNfeDetails(response.data)
      } else {
        showToast('Erro ao carregar detalhes da NF-e', 'error')
        setViewModalOpen(false)
      }
    } catch (error) {
      console.error('Erro ao carregar detalhes da NF-e:', error)
      showToast('Erro ao carregar detalhes da NF-e', 'error')
      setViewModalOpen(false)
    } finally {
      setLoadingDetails(false)
    }
  }

  const formatDate = (date) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('pt-BR')
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  // Mapear código de forma de pagamento para nome legível
  const getFormaPagamentoNome = (codigo) => {
    const formasPagamento = {
      '01': 'Dinheiro',
      '02': 'Cheque',
      '03': 'Cartão de Crédito',
      '04': 'Cartão de Débito',
      '05': 'Crédito Loja',
      '10': 'Vale Alimentação',
      '11': 'Vale Refeição',
      '12': 'Vale Presente',
      '13': 'Vale Combustível',
      '14': 'Duplicata Mercantil',
      '15': 'Boleto Bancário',
      '16': 'Depósito Bancário',
      '17': 'PIX',
      '18': 'Transferência Bancária',
      '19': 'Programa de Fidelidade',
      '90': 'Sem Pagamento',
      '99': 'Outros'
    }
    return formasPagamento[codigo] || `Forma ${codigo || 'Não informada'}`
  }

  const columns = [
    {
      key: 'number',
      label: 'Número',
      render: (value, item) => {
        if (!item) return '-'
        return <span className="nfe-number">#{item.number || value}</span>
      }
    },
    {
      key: 'customer_name',
      label: 'Cliente',
      render: (value, item) => {
        if (!item) return '-'
        return item.customer_name || value || '-'
      }
    },
    {
      key: 'issue_date',
      label: 'Data Emissão',
      render: (value, item) => {
        if (!item) return '-'
        return formatDate(item.issue_date || value)
      }
    },
    {
      key: 'access_key',
      label: 'Chave de Acesso',
      render: (value, item) => {
        if (!item || !item.access_key) return '-'
        const chave = item.access_key
        return (
          <span className="access-key" title={chave}>
            {chave.substring(0, 20)}...
          </span>
        )
      }
    },
    {
      key: 'total_amount',
      label: 'Valor Total',
      render: (value, item) => {
        if (!item) return '-'
        return formatCurrency(item.total_amount || value || 0)
      }
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, item) => {
        if (!item) return '-'
        return <NFeStatusBadge status={item.status || value} />
      }
    },
    {
      key: 'actions',
      label: 'Ações',
      render: (value, item) => {
        if (!item) return null
        
        const handleDropdownToggle = (e) => {
          e.stopPropagation()
          if (openDropdown === item.id) {
            setOpenDropdown(null)
          } else {
            setOpenDropdown(item.id)
            // Salvar referência do botão
            setDropdownButtonRefs(prev => ({
              ...prev,
              [item.id]: e.currentTarget
            }))
          }
        }

        const handleAction = (action, nfe) => {
          setOpenDropdown(null)
          switch (action) {
            case 'view':
              handleViewNFe(nfe)
              break
            case 'edit':
              navigate(`/erp/vendas/nfe/${nfe.id}/editar`)
              break
            case 'transmit':
              handleTransmitNFe(nfe)
              break
            case 'consultar-status':
              handleConsultarStatus(nfe)
              break
            case 'visualizar-danfe':
              handleVisualizarDANFE(nfe)
              break
            case 'corrigir-razao-social':
              setSelectedNFe(nfe)
              setCorrigirRazaoModalOpen(true)
              break
            case 'cancel':
              handleCancel(nfe)
              break
            default:
              break
          }
        }

        return (
          <div className="actions-cell">
            <div className="dropdown-wrapper">
              <button
                ref={(el) => {
                  if (el && !dropdownButtonRefs[item.id]) {
                    setDropdownButtonRefs(prev => ({
                      ...prev,
                      [item.id]: el
                    }))
                  }
                }}
                className="btn-action-icon"
                onClick={handleDropdownToggle}
                title="Ações"
              >
                <MoreVertical size={18} />
              </button>
              {openDropdown === item.id && (
                <NFeDropdownMenu
                  nfe={item}
                  onAction={handleAction}
                  onClose={() => setOpenDropdown(null)}
                  buttonRef={dropdownButtonRefs[item.id]}
                />
              )}
            </div>
          </div>
        )
      }
    }
  ]

  return (
    <div className="nfe-list-page">
      <div className="page-header">
        <div>
          <h1>Notas Fiscais Eletrônicas (NF-e)</h1>
          <p>Reenvio e acompanhamento das NF-e emitidas pelo caixa</p>
        </div>
      </div>

      <div className="nfe-list-table-container">
        <div className="nfe-list-table-header">
          <div className="nfe-list-search-wrapper">
            <Search className="nfe-list-search-icon" size={18} />
            <input 
              type="text" 
              placeholder="Pesquisar NF-e..." 
              className="nfe-list-search-input"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>
        </div>

        <div className="nfe-list-table-wrapper">
          <DataTable
            data={nfeList}
            columns={columns}
            loading={loading}
            emptyMessage="Nenhuma NF-e encontrada"
            pagination={pagination.totalPages > 0 ? {
              from: ((pagination.page - 1) * pagination.limit) + 1,
              to: Math.min(pagination.page * pagination.limit, pagination.total),
              total: pagination.total,
              current: pagination.page,
              totalPages: pagination.totalPages,
              hasPrev: pagination.page > 1,
              hasNext: pagination.page < pagination.totalPages,
              onPrev: () => setPagination(prev => ({ ...prev, page: prev.page - 1 })),
              onNext: () => setPagination(prev => ({ ...prev, page: prev.page + 1 }))
            } : null}
          />
        </div>
      </div>

      <NFeCancelModal
        isOpen={cancelModalOpen}
        onClose={() => {
          setCancelModalOpen(false)
          setSelectedNFe(null)
        }}
        onConfirm={handleConfirmCancel}
        nfe={selectedNFe}
        loading={cancelling}
        initialJustificativa={cancelInitialJustification}
      />

      <NFeCorrigirRazaoSocialModal
        isOpen={corrigirRazaoModalOpen}
        onClose={() => {
          setCorrigirRazaoModalOpen(false)
        }}
        onConfirm={handleConfirmRazaoSocial}
        onRequestCancel={() => {
          setCorrigirRazaoModalOpen(false)
          setCancelInitialJustification(RAZAO_SOCIAL_CANCEL_JUSTIFICATION)
          setCancelModalOpen(true)
        }}
        nfe={selectedNFe}
        loading={savingRazaoSocial}
      />

      <NFeCartaCorrecaoModal
        isOpen={cartaCorrecaoModalOpen}
        onClose={() => {
          setCartaCorrecaoModalOpen(false)
          setSelectedNFe(null)
        }}
        onConfirm={handleConfirmCartaCorrecao}
        nfe={selectedNFe}
        loading={emittingCCe}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
      />

      {/* Modal de Visualização */}
      <Modal
        isOpen={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false)
          setSelectedNFe(null)
          setNfeDetails(null)
        }}
        title="Detalhes da NF-e"
        size="large"
        footer={
          <button
            className="btn-close-modal"
            onClick={() => {
              setViewModalOpen(false)
              setSelectedNFe(null)
              setNfeDetails(null)
            }}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 500
            }}
          >
            Fechar
          </button>
        }
      >
        {loadingDetails ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p>Carregando detalhes da NF-e...</p>
          </div>
        ) : nfeDetails ? (
          <div className="nfe-details-modal" style={{ padding: '1rem' }}>
            {/* Informações Gerais */}
            <div className="details-section" style={{ marginBottom: '2rem' }}>
              <h3 className="section-title" style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem', color: '#1f2937' }}>
                Informações Gerais
              </h3>
              <div className="details-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                <div className="detail-item">
                  <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                    Número da NF-e:
                  </label>
                  <span style={{ fontSize: '1rem', color: '#1f2937' }}>#{nfeDetails.number || nfeDetails.id}</span>
                </div>
                {nfeDetails.series && (
                  <div className="detail-item">
                    <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                      Série:
                    </label>
                    <span style={{ fontSize: '1rem', color: '#1f2937' }}>{nfeDetails.series}</span>
                  </div>
                )}
                <div className="detail-item">
                  <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                    Modelo:
                  </label>
                  <span style={{ fontSize: '1rem', color: '#1f2937' }}>{nfeDetails.model || '55'}</span>
                </div>
                <div className="detail-item">
                  <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                    Status:
                  </label>
                  <span><NFeStatusBadge status={nfeDetails.status} /></span>
                </div>
                <div className="detail-item">
                  <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                    Data de Emissão:
                  </label>
                  <span style={{ fontSize: '1rem', color: '#1f2937' }}>{formatDate(nfeDetails.issue_date)}</span>
                </div>
                {nfeDetails.access_key && (
                  <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                      Chave de Acesso:
                    </label>
                    <span style={{ fontSize: '0.875rem', color: '#1f2937', fontFamily: 'monospace' }}>{nfeDetails.access_key}</span>
                  </div>
                )}
                <div className="detail-item">
                  <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                    Valor Total:
                  </label>
                  <span className="amount-value" style={{ fontSize: '1.25rem', fontWeight: 600, color: '#3b82f6' }}>
                    {formatCurrency(nfeDetails.total_amount)}
                  </span>
                </div>
              </div>
            </div>

            {/* Cliente */}
            {nfeDetails.customer_name && (
              <div className="details-section" style={{ marginBottom: '2rem' }}>
                <h3 className="section-title" style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem', color: '#1f2937' }}>
                  Cliente
                </h3>
                <div className="details-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                  <div className="detail-item">
                    <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                      Nome:
                    </label>
                    <span style={{ fontSize: '1rem', color: '#1f2937' }}>{nfeDetails.customer_name}</span>
                  </div>
                  {nfeDetails.customer_cpf_cnpj && (
                    <div className="detail-item">
                      <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                        CPF/CNPJ:
                      </label>
                      <span style={{ fontSize: '1rem', color: '#1f2937' }}>{nfeDetails.customer_cpf_cnpj}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Formas de Pagamento */}
            {nfeDetails.payments && nfeDetails.payments.length > 0 && (
              <div className="details-section" style={{ marginBottom: '2rem' }}>
                <h3 className="section-title" style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem', color: '#1f2937' }}>
                  Formas de Pagamento
                </h3>
                <div className="details-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                  {nfeDetails.payments.map((payment, index) => (
                    <div key={payment.id || index} className="detail-item" style={{ gridColumn: index % 2 === 0 && nfeDetails.payments.length === 1 ? '1 / -1' : 'auto' }}>
                      <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                        {nfeDetails.payments.length > 1 ? `Pagamento ${index + 1}:` : 'Forma de Pagamento:'}
                      </label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <span style={{ fontSize: '1rem', color: '#1f2937', fontWeight: 500 }}>
                          {payment.payment_receipt_method?.name || 
                           (payment.payment_method_id && getFormaPagamentoNome(payment.payment_method_id.toString().padStart(2, '0'))) ||
                           'Não informado'}
                        </span>
                        <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          {formatCurrency(payment.amount)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Itens da NF-e */}
            {nfeDetails.items && nfeDetails.items.length > 0 && (
              <div className="details-section">
                <h3 className="section-title" style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem', color: '#1f2937' }}>
                  Itens da NF-e ({nfeDetails.items.length})
                </h3>
                <div className="items-table-wrapper" style={{ overflowX: 'auto' }}>
                  <table className="items-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem' }}>Produto</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem' }}>SKU</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600, fontSize: '0.875rem' }}>Quantidade</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600, fontSize: '0.875rem' }}>Valor Unit.</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600, fontSize: '0.875rem' }}>Desconto</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600, fontSize: '0.875rem' }}>Valor Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nfeDetails.items.map((item, index) => (
                        <tr key={item.id || index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '0.75rem' }}>{item.product_name || `Produto #${item.product_id}`}</td>
                          <td style={{ padding: '0.75rem' }}>{item.sku || '-'}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>{parseFloat(item.quantity || 0).toFixed(3).replace('.', ',')}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(item.unit_price)}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(item.discount_amount || 0)}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 500 }}>{formatCurrency(item.total_price)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: '2px solid #e5e7eb' }}>
                        <td colSpan="5" style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>
                          Total:
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600, fontSize: '1.1rem', color: '#3b82f6' }}>
                          {formatCurrency(nfeDetails.total_amount)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {(!nfeDetails.items || nfeDetails.items.length === 0) && (
              <div className="details-section">
                <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>
                  Nenhum item cadastrado nesta NF-e.
                </p>
              </div>
            )}

            {/* Observações */}
            {nfeDetails.observation && (
              <div className="details-section" style={{ marginTop: '2rem' }}>
                <h3 className="section-title" style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem', color: '#1f2937' }}>
                  Observações
                </h3>
                <p style={{ color: '#1f2937', padding: '1rem', background: '#f9fafb', borderRadius: '8px', whiteSpace: 'pre-wrap' }}>
                  {nfeDetails.observation}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p>Erro ao carregar detalhes da NF-e.</p>
          </div>
        )}
      </Modal>

      {/* Modal de Visualização do DANFE */}
      {danfeModalOpen && (
        <div className="nfe-danfe-modal-overlay" onClick={handleCloseDanfeModal}>
          <div className="nfe-danfe-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="nfe-danfe-modal-header">
              <div className="nfe-danfe-modal-title-content">
                <h2>DANFE - NF-e #{danfeNFeNumber || 'N/A'}</h2>
                <span className="nfe-danfe-modal-subtitle">Documento Auxiliar da Nota Fiscal Eletrônica</span>
              </div>
              <button
                className="nfe-danfe-close-btn"
                onClick={handleCloseDanfeModal}
                title="Fechar"
              >
                <X size={20} />
              </button>
            </div>
            <div className="nfe-danfe-modal-body">
              {danfeLoading && (
                <div className="nfe-danfe-loading">
                  <Loader2 size={32} className="nfe-danfe-spinner" />
                  <p>Carregando DANFE...</p>
                </div>
              )}

              {danfeError && (
                <div className="nfe-danfe-error">
                  <AlertCircle size={24} />
                  <p>{danfeError}</p>
                  {danfeUrl && (
                    <button
                      className="nfe-danfe-retry-btn"
                      onClick={handleRetryDanfe}
                    >
                      Tentar novamente
                    </button>
                  )}
                </div>
              )}

              {danfeUrl && !danfeError && (danfeBlobUrl || !danfeUrl.includes('/api/')) && (
                <iframe
                  id="danfe-iframe"
                  src={danfeBlobUrl || danfeUrl}
                  className="nfe-danfe-iframe"
                  title="DANFE"
                  onLoad={() => setDanfeLoading(false)}
                  onError={() => {
                    setDanfeLoading(false)
                    setDanfeError('Erro ao carregar o DANFE. Verifique se a URL está acessível.')
                  }}
                  style={{ display: danfeLoading ? 'none' : 'block' }}
                />
              )}
              
              {danfeUrl && !danfeBlobUrl && danfeUrl.includes('/api/') && danfeLoading && (
                <div className="nfe-danfe-loading">
                  <Loader2 size={32} className="nfe-danfe-spinner" />
                  <p>Baixando PDF...</p>
                </div>
              )}

              {!danfeUrl && (
                <div className="nfe-danfe-error">
                  <AlertCircle size={24} />
                  <p>URL do DANFE não disponível.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Toast
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={hideToast}
        duration={4000}
      />
    </div>
  )
}

export default NFeListPage
